//post
// comments
// tags
// relations
import {computed, ref} from "vue";

const useConnector = () => {

  //このミリ秒数間は connectPost を連続で叩けなくする
  const REJECT_POST_MILLI_SECONDS = 500;

  //主にサーバーから提供されるエラーを文字列の一次元配列にしたもの
  const errors = ref([]);

  //最後にサーバーと POST 通信を行った際のタイムスタンプ(ミリ秒)
  const _lastPostConnectTime = ref(0);

  //連打防止によるエラーか
  const isDoubleConnectBlock = computed(() => errors.value.length === 1 && errors.value[0] === '');

  /**
   * method により指定された http method にて endpoint に対し ajax 通信を行う
   * エンドポイントの返り値は JSON であると見なされ、パースしたデータが Promise でラップされ返される
   * サーバーより失敗が返ってきた場合は Promise でラップされた null が返される (reject はされないので、try, catch は必要無い)
   * 代わりに null が返された場合は errors でエラー文字列の一次元配列を手に入れられる
   * @param endpoint
   * @param method
   * @param body
   * @returns {Promise<any|null>}
   * @private
   */
  const _connect = async (endpoint, method = 'get', body = null) => {
    //サーバーと通信を試みる
    try {
      //サーバーと適切なメソッドで通知を行う
      const response = method === 'post'
        ? await fetch(endpoint, {
          method: 'post',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body
        })
        : await fetch(endpoint);

      //レスポンス異常発生
      if ( ! response.ok) {
        //もし 404 だったらログインしていない
        if (response.status === 404) {
          throw new Error('ログインセッションが切れている可能性があります。恐れ入りますがページをリロードして再度ログインしてください。');
        }

        //エラーメッセージが返っているか判定
        const parameter = await response.json();

        //もしエラーメッセージが存在すれば返す
        if ('errors' in parameter && Array.isArray(parameter.errors)) {
          throw parameter.errors;
        }
        if ('errors' in parameter && typeof parameter.errors === 'object' && parameter.errors !== null) {
          throw Object.keys(parameter.errors).map(key => parameter.errors[key].join("\n"));
        }
        if ('messages' in parameter && Array.isArray(parameter.messages)) {
          throw parameter.messages;
        }

        //もし単発エラーがあれば返す
        if ('message' in parameter && typeof parameter.message === 'string') {
          throw [parameter.message];
        }

        //エラーメッセージを文字列に変換して例外を投げる
        throw new Error('エラーが発生しました。しばらくしてから再度お試しください。');
      }

      //パラメータを得る
      const parameter = await response.json();

      //返却
      return parameter;
    }

      //エラーが起きたら文字列の一次元配列に成形して errors に登録する
    catch (error) {
      //恐らく通信途絶
      if (error instanceof TypeError) {
        errors.value = ['ネットワーク接続に失敗しました。'];
      }

      //恐らく意図的な中断
      else if (error instanceof DOMException) {
        errors.value = ['ネットワーク接続が中断されました。'];
      }

      //恐らくサーバーからのメッセージ
      else if (Array.isArray(error)) {
        errors.value = error;
      }

      //恐らくカスタムエラー
      else if (error instanceof Error) {
        errors.value = [error.message];
      }

      //不明
      else {
        errors.value = ['エラーが発生しました。しばらくしてから再度お試しください。'];
      }

      //失敗として null を返す
      return null;
    }
  };

  /**
   * サーバーと POST 通信する
   * @param endpoint
   * @param body
   * @returns {Promise<any>}
   * @private
   */
  const connectPost = async (endpoint, body = null) => {
    //現在のタイムスタンプを得る
    const time = (new Date()).getTime();

    //前回の POST 通知から REJECT_POST_MILLI_SECONDS ミリ秒経過していなければ拒否する
    if (time - _lastPostConnectTime.value < REJECT_POST_MILLI_SECONDS) {
      //エラーを空文字だけとする
      errors.value = [''];

      //失敗を返す
      return null;
    }

    //最終 POST 開始時刻を更新する
    _lastPostConnectTime.value = time;

    //サーバーと通知を開始
    const result = await _connect(endpoint, 'post', body);

    //結果を返す
    return result;
  }

  /**
   * サーバーと GET 通信する
   * @param endpoint
   * @returns {Promise<any>}
   * @private
   */
  const connectGet = async (endpoint) => {
    //サーバーと通知を開始
    const result = await _connect(endpoint, 'get');

    //結果を返す
    return result;
  }

  //提供
  return {
    errors,
    isDoubleConnectBlock,
    connectGet,
    connectPost
  };

};

//composition のエクスポート
export default useConnector;

//composition を注入するためのキー
export const USE_CONNECTOR_KEY = Symbol('useConnector');
